import { deleteResource, getTimezones } from '@/lib/api/resource'
import { getResourceTypes } from '@/lib/api/resourceType'
import { fetchResourcePagedData, manageCreateResource, manageUpdateResource } from '@/lib/services/resource'
import { Timezone, type Resource } from '@/lib/types/resource'
import { ResourceType } from '@/lib/types/resourceType'
import { defineStore } from 'pinia'

interface ResourceState {
  resources: Resource[]
  types: ResourceType[]
  timezones: Timezone[]
  // True once /timezones has answered successfully at least once. The degraded-timezone gate
  // (UI-SPEC E4, D-09) must read `timezonesLoaded && resource.timezone === null`, never "the list is
  // empty": `timezones` is [] both BEFORE the fetch and AFTER a failure, so an empty-list test
  // cannot tell a cold cache from an unresolvable id and would flash the amber "unrecognised time
  // zone" warning on every resource for as long as the request is in flight.
  timezonesLoaded: boolean
  // True when the last /timezones attempt FAILED, as opposed to never having run. `timezonesLoaded`
  // alone cannot tell those two apart, and they need different treatment: a cold cache resolves
  // itself, a failure does not. Read this to drive a distinct, non-accusatory "could not load the
  // time zone list" notice — the D-09 amber warning is the wrong message for it, because it accuses
  // the RESOURCE of bad configuration when the truth is that the list never arrived.
  timezonesFetchFailed: boolean
  loading: boolean
}

export const useResourcesStore = defineStore('resources', {
  state: (): ResourceState => ({
    resources: [],
    types: [],
    timezones: [],
    timezonesLoaded: false,
    timezonesFetchFailed: false,
    loading: false
  }),
  actions: {
    async _initialize() {
      await Promise.all([
        this.fetchResourceTypes(),
        this.fetchTimezones()
      ])
    },

    async fetchResourceTypes(): Promise<void> {
      this.loading = true

      try {
        const res = await getResourceTypes()
        this.types = res.items
      } catch (err) {
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    async fetchTimezones(): Promise<void> {
      this.loading = true

      try {
        await this._loadTimezones()
      } finally {
        this.loading = false
      }
    },

    // The request itself, kept separate from `loading` so ensureTimezonesLoaded can retry from
    // inside another in-flight store action without flipping the spinner off underneath it.
    async _loadTimezones(): Promise<void> {
      try {
        this.timezones = await getTimezones()
        // Set only on success, and deliberately NOT reset in the catch: a failed fetch leaves the
        // flag false, which is what keeps the E4 gate from claiming a resource's zone is
        // unrecognised when the truth is that the list never arrived (D-09).
        this.timezonesLoaded = true
        this.timezonesFetchFailed = false

        // Re-resolve anything already mapped against an older (or empty) list. dtoToResource
        // snapshots `timezones` at map time, so without this a later success would never repair a
        // resource that was mapped while the list was empty — it would keep reporting a null
        // timezone forever even though the id is perfectly resolvable.
        this.resources = this.resources.map(r => ({ ...r, timezone: this.timezones.find(tz => tz.id === r.timezoneId) ?? null }))
      } catch (err) {
        console.error(err)
        this.timezonesFetchFailed = true
      }
    },

    // Recovery point. fetchTimezones' only caller is _initialize(), which initializeAppStores()
    // memoises into storesInitPromise for the life of the tab, so before this existed a single
    // transient failure at login was permanent for the whole session: `timezones` stayed [] and
    // `timezonesLoaded` stayed false. Every D-09 gate reads that flag, so ALL of them stayed
    // suppressed and the application ran on browser-local time with no warning on any surface —
    // the exact silent degradation this phase exists to remove, reachable from one dropped request.
    //
    // Cheap by construction: a no-op once the list has loaded.
    async ensureTimezonesLoaded(): Promise<void> {
      if (this.timezonesLoaded) return
      await this._loadTimezones()
    },

    async fetchPagedData(page: number = 1, pageSize: number, sortField?: string, sortOrder?: number, filters?: Record<string, any>): Promise<PagedResultMetadata> {
      this.loading = true

      try {
        const params: PagedRequestParams = {
          offset: (page - 1) * pageSize,
          limit: pageSize,
          sortField: sortField,
          sortOrder: sortOrder && sortOrder === 1 ? 'ASC' : 'DESC',
          filters: filters
        }

        const res = await fetchResourcePagedData(params)
        const { items, ...metadata } = res
        this.resources = items

        return metadata as PagedResultMetadata
      } catch (err) {
        console.error(err)
        throw err
      } finally {
        this.loading = false
      }
    },

    async fetchAll(): Promise<void> {
      this.loading = true

      try {
        const res = await fetchResourcePagedData()
        const { items } = res
        this.resources = items
      } catch (err) {
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    async createOne(payload: Resource): Promise<ErrorMessage | Resource> {
      const created = await manageCreateResource(payload)

      if (created && 'errorCode' in created) {
        return created
      }

      this.resources.push(created)
      return created
    },

    async updateOne(payload: Resource): Promise<ErrorMessage | void> {
      const updated = await manageUpdateResource(payload)

      if (updated && 'errorCode' in updated) {
        return updated
      }

      const i = this.resources.findIndex(r => r.id === updated.id)
      if (i !== -1) this.resources[i] = updated
    },

    async deleteOne(id: string): Promise<ErrorMessage | void> {
      const res = await deleteResource(id)

      if (res && 'errorCode' in res) {
        return res
      }

      this.resources = this.resources.filter(r => r.id !== id)
    }
  },

  getters: {
    getResourceById: (state) => {
      return (id: string): Resource | undefined => {
        return state.resources.find(r => r.id === id)
      }
    },
    getResourceTypeById: (state) => {
      return (id: string): ResourceType | undefined => {
        return state.types.find(t => t.id === id)
      }
    },
    getResourceDisplayNameById: (state) => {
      return (id: string): string | undefined => {
        const resource = state.resources.find(r => r.id === id)
        if (!resource) return undefined
        return resource.name + ' (' + resource.location + ')'
      }
    }
  },
})
